// Shared hashtag helpers — keeps the matching rules identical wherever a
// hashtag is rendered, counted, or looked up (PostCard, Feed, HashtagPage,
// TrendingModal). Keep this regex in sync with the one in PostCard.jsx.
const HASHTAG_REGEX = /#[a-zA-Z0-9_]+/g

// Pull every hashtag out of a post's text content, e.g. "gm #web3 #gm" -> ["#web3", "#gm"]
export function extractHashtags(content) {
  if (!content || typeof content !== 'string') return []
  return content.match(HASHTAG_REGEX) || []
}

// Normalize a tag for comparisons/URLs — always lowercase, no leading "#".
export function normalizeTag(tag) {
  return (tag || '').replace(/^#/, '').toLowerCase()
}

// Does this post contain the given hashtag? Comparison is case-insensitive
// and tolerant of the tag being passed with or without a leading "#".
export function postMatchesHashtag(post, tag) {
  const target = normalizeTag(tag)
  if (!target) return false
  return extractHashtags(post?.content).some((h) => normalizeTag(h) === target)
}

function formatCompactCount(value) {
  const num = Number(value ?? 0)
  if (!Number.isFinite(num)) return '0'
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1)}M`
  if (num >= 1_000) return `${(num / 1_000).toFixed(num >= 10_000 ? 0 : 1)}k`
  return `${num}`
}

// Build a trending list straight from whatever posts we already have loaded
// on the client. Used as a fallback since the backend doesn't currently
// expose a dedicated trending-hashtags endpoint. Each hashtag is only
// counted once per post (so a post spamming the same tag doesn't skew it).
export function computeTrendingFromPosts(posts, limit = 10) {
  const counts = new Map()

  for (const post of posts || []) {
    const tagsInPost = new Set(extractHashtags(post?.content).map((h) => normalizeTag(h)))
    for (const key of tagsInPost) {
      const existing = counts.get(key)
      if (existing) {
        existing.postCount += 1
      } else {
        // Keep the first-seen casing for display (e.g. "#Web3" vs "#web3")
        const original = extractHashtags(post.content).find((h) => normalizeTag(h) === key)
        counts.set(key, { hashtag: original || `#${key}`, postCount: 1 })
      }
    }
  }

  return Array.from(counts.values())
    .sort((a, b) => b.postCount - a.postCount)
    .slice(0, limit)
    .map((t, i) => ({
      id: `${t.hashtag}-${i}`,
      hashtag: t.hashtag,
      title: t.hashtag.replace(/^#/, ''),
      postCount: formatCompactCount(t.postCount),
    }))
}
